import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import type { HistoryItem, RecipientDetail } from "../types";
import { STATUS_CONFIG, TYPE_CONFIG } from "../types";

interface Props {
  item: HistoryItem | null;
  onClose: () => void;
  onDeleted?: () => void;
}

export default function HistoryDetailModal({ item, onClose, onDeleted }: Props) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [recipientFilter, setRecipientFilter] = useState<"all" | "success" | "fail">("all");
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [recipients, setRecipients] = useState<RecipientDetail[]>([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  useEffect(() => {
    if (!item) {
      setResent(false);
      setResending(false);
      setCancelConfirm(false);
      setRecipientFilter("all");
      setDeleting(false);
      setDeleteConfirm(false);
    }
  }, [item]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  // 모달 열릴 때 수신자 목록 lazy-load
  useEffect(() => {
    if (!item || item.status === "scheduled") {
      setRecipients([]);
      setRecipientsLoading(false);
      return;
    }
    setRecipients([]);
    setRecipientsLoading(true);
    let cancelled = false;

    supabase.functions.invoke("get-send-recipients", {
      body: { history_id: item.id },
    }).then(({ data, error }) => {
      if (cancelled) return;
      if (!error && data?.recipients) {
        setRecipients((data.recipients as any[]).map((r: any) => ({
          name: r.name ?? undefined,
          phone: r.phone,
          status: r.status === "failed" ? "fail" : r.status,
          batchIndex: r.batch_index,
          failReason: r.fail_reason ?? undefined,
        })));
      }
    }).catch((err) => {
      if (!cancelled) console.error("[HistoryDetailModal] Failed to load recipients:", err);
    }).finally(() => {
      if (!cancelled) setRecipientsLoading(false);
    });

    return () => { cancelled = true; };
  }, [item?.id, item?.status]);

  if (!item) return null;

  const statusCfg = STATUS_CONFIG[item.status];
  const typeCfg = TYPE_CONFIG[item.type];
  const totalCount = item.totalCount;
  const successRate = totalCount > 0 && item.status !== "scheduled"
    ? Math.round((item.successCount / totalCount) * 100)
    : null;

  const filteredRecipients =
    recipientFilter === "all"
      ? recipients
      : recipients.filter((r) => r.status === recipientFilter);

  const handleResend = () => {
    setResending(true);
    setTimeout(() => {
      setResending(false);
      setResent(true);
    }, 1500);
  };

  const handleResendToFailed = () => {
    navigate("/dashboard/send");
    onClose();
  };

  const handleCancel = () => {
    setCancelConfirm(false);
    onClose();
  };

  const handleDelete = async () => {
    if (!item || !user || deleting) return;
    setDeleting(true);
    try {
      const { data, error } = await supabase.rpc("delete_send_histories", {
        p_user_id: user.id,
        p_ids: [item.id],
      });
      if (error) {
        console.error("[HistoryDetailModal] delete error:", error);
        alert(`삭제 중 오류가 발생했습니다: ${error.message}`);
        return;
      }
      if (data?.deleted > 0) {
        onDeleted?.();
        onClose();
      }
    } catch (err: any) {
      console.error("[HistoryDetailModal] delete exception:", err);
      alert(`삭제 중 오류가 발생했습니다: ${err.message || "알 수 없는 오류"}`);
    } finally {
      setDeleting(false);
      setDeleteConfirm(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>

      {/* Modal */}
      <div className="relative w-full md:max-w-2xl max-h-[92vh] md:max-h-[90vh] bg-[#0d0d1a] border border-white/[0.1] rounded-t-2xl md:rounded-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-4 md:p-6 border-b border-white/[0.08] shrink-0">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-lg md:rounded-xl bg-gradient-to-br from-violet-500/30 to-pink-500/30">
              <i className="ri-message-2-line text-violet-400 text-base md:text-lg"></i>
            </div>
            <div>
              <div className="flex items-center gap-1.5 md:gap-2 mb-0.5">
                <span className={`text-[10px] md:text-xs font-bold px-1.5 py-0.5 md:px-2 md:py-0.5 rounded-md ${typeCfg.className}`}>{item.type}</span>
                <span className={`inline-flex items-center gap-1 text-[10px] md:text-xs font-medium px-1.5 py-0.5 md:px-2 md:py-0.5 rounded-full ${statusCfg.className}`}>
                  <span className={`w-1 md:w-1.5 h-1 md:h-1.5 rounded-full ${statusCfg.dotClass}`}></span>
                  {statusCfg.label}
                </span>
              </div>
              <p className="text-gray-400 text-[10px] md:text-xs">{item.sentAt}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-gray-500 hover:text-white cursor-pointer transition-colors rounded-lg hover:bg-white/[0.06]"
          >
            <i className="ri-close-line text-base md:text-lg"></i>
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 md:space-y-5">
          {/* Stats row */}
          <div className="grid grid-cols-3 gap-2 md:gap-3">
            <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-3 md:p-4 text-center">
              <p className="text-xl md:text-2xl font-bold text-white">{totalCount.toLocaleString()}</p>
              <p className="text-gray-500 text-[10px] md:text-xs mt-0.5 md:mt-1">총 수신자</p>
            </div>
            <div className="bg-emerald-500/[0.06] border border-emerald-500/20 rounded-xl p-3 md:p-4 text-center">
              <p className="text-xl md:text-2xl font-bold text-emerald-400">{item.successCount.toLocaleString()}</p>
              <p className="text-gray-500 text-[10px] md:text-xs mt-0.5 md:mt-1">성공</p>
            </div>
            <div className={`rounded-xl p-3 md:p-4 text-center border ${item.failCount > 0 ? "bg-red-500/[0.06] border-red-500/20" : "bg-white/[0.03] border-white/[0.07]"}`}>
              <p className={`text-xl md:text-2xl font-bold ${item.failCount > 0 ? "text-red-400" : "text-gray-600"}`}>
                {item.failCount.toLocaleString()}
              </p>
              <p className="text-gray-500 text-[10px] md:text-xs mt-0.5 md:mt-1">실패</p>
            </div>
          </div>

          {/* Success rate bar */}
          {successRate !== null && (
            <div>
              <div className="flex items-center justify-between mb-1 md:mb-1.5">
                <span className="text-gray-400 text-[10px] md:text-xs font-medium">발송 성공률</span>
                <span className={`text-xs md:text-sm font-bold ${successRate >= 90 ? "text-emerald-400" : successRate >= 70 ? "text-amber-400" : "text-red-400"}`}>
                  {successRate}%
                </span>
              </div>
              <div className="h-1.5 md:h-2 bg-white/[0.06] rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-700 ${
                    successRate >= 90 ? "bg-gradient-to-r from-emerald-500 to-teal-500" :
                    successRate >= 70 ? "bg-gradient-to-r from-amber-500 to-yellow-500" :
                    "bg-gradient-to-r from-red-500 to-rose-500"
                  }`}
                  style={{ width: `${successRate}%` }}
                />
              </div>
            </div>
          )}

          {/* Batch breakdown */}
          {recipients.length > 0 && (() => {
            const maxBatch = Math.max(...recipients.map((r) => r.batchIndex ?? 0));
            if (maxBatch <= 0) return null;
            const batches: { idx: number; total: number; success: number; fail: number }[] = [];
            for (let i = 0; i <= maxBatch; i++) {
              const batchRecipients = recipients.filter((r) => (r.batchIndex ?? 0) === i);
              batches.push({
                idx: i,
                total: batchRecipients.length,
                success: batchRecipients.filter((r) => r.status === "success").length,
                fail: batchRecipients.filter((r) => r.status === "fail").length,
              });
            }
            return (
              <div>
                <div className="flex items-center gap-1.5 md:gap-2 mb-2 md:mb-3">
                  <div className="w-4 h-4 md:w-5 md:h-5 flex items-center justify-center">
                    <i className="ri-stack-line text-violet-400 text-xs md:text-sm"></i>
                  </div>
                  <p className="text-gray-400 text-[10px] md:text-xs font-medium">배치별 발송 결과 ({batches.length}개 배치)</p>
                </div>
                <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl overflow-hidden">
                  <div className="max-h-40 md:max-h-48 overflow-y-auto">
                    {batches.map((batch) => {
                      const rate = batch.total > 0 ? Math.round((batch.success / batch.total) * 100) : 0;
                      return (
                        <div
                          key={batch.idx}
                          className="flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 border-b border-white/[0.04] last:border-0 hover:bg-white/[0.02] transition-colors"
                        >
                          <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-lg bg-white/[0.05] shrink-0">
                            <span className="text-white text-[10px] md:text-xs font-bold">#{batch.idx + 1}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-white text-[10px] md:text-xs font-medium">{batch.total}명</span>
                              <span className={`text-[10px] md:text-xs font-semibold ${rate === 100 ? "text-emerald-400" : rate >= 80 ? "text-amber-400" : "text-red-400"}`}>
                                {rate}%
                              </span>
                            </div>
                            <div className="h-1 md:h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${
                                  rate === 100 ? "bg-emerald-500" : rate >= 80 ? "bg-amber-500" : "bg-red-500"
                                }`}
                                style={{ width: `${rate}%` }}
                              />
                            </div>
                            <div className="flex items-center gap-2 md:gap-3 mt-1">
                              <span className="text-emerald-400 text-[10px] md:text-xs">성공 {batch.success}</span>
                              {batch.fail > 0 && (
                                <span className="text-red-400 text-[10px] md:text-xs">실패 {batch.fail}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Message info */}
          <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-3 md:p-4 space-y-2 md:space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-gray-400 text-[10px] md:text-xs font-medium">발신번호</p>
              <p className="text-white text-xs md:text-sm font-medium">{item.senderNumber}</p>
            </div>
            {item.subject && (
              <div className="flex items-center justify-between">
                <p className="text-gray-400 text-[10px] md:text-xs font-medium">제목</p>
                <p className="text-white text-xs md:text-sm font-medium">{item.subject}</p>
              </div>
            )}
            <div className="flex items-center justify-between">
              <p className="text-gray-400 text-[10px] md:text-xs font-medium">비용</p>
              <p className="text-amber-400 text-xs md:text-sm font-bold">₩{item.cost.toLocaleString()}</p>
            </div>
            {item.refundAmount != null && item.refundAmount > 0 && (
              <div className="flex items-center justify-between">
                <p className="text-gray-400 text-[10px] md:text-xs font-medium">환불</p>
                <p className="text-emerald-400 text-xs md:text-sm font-bold">
                  <i className="ri-refund-2-line text-xs mr-1"></i>
                  +₩{item.refundAmount.toLocaleString()}
                </p>
              </div>
            )}
            {item.refundAmount != null && item.refundAmount > 0 && (
              <div className="flex items-center justify-between border-t border-white/[0.07] pt-2 md:pt-2.5 mt-1">
                <p className="text-gray-300 text-[10px] md:text-xs font-medium">실제 과금</p>
                <p className="text-white text-xs md:text-sm font-bold">
                  ₩{Math.max(0, item.cost - item.refundAmount).toLocaleString()}
                </p>
              </div>
            )}
            {item.scheduledAt && (
              <div className="flex items-center justify-between">
                <p className="text-gray-400 text-[10px] md:text-xs font-medium">예약 시각</p>
                <p className="text-violet-400 text-xs md:text-sm font-medium">{item.scheduledAt}</p>
              </div>
            )}
          </div>

          {/* Message content */}
          <div>
            <p className="text-gray-400 text-[10px] md:text-xs font-medium mb-1.5 md:mb-2">메시지 내용</p>
            <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-3 md:p-4">
              {item.hasImage && (
                <div className="flex items-center gap-1.5 md:gap-2 mb-2 md:mb-3 pb-2 md:pb-3 border-b border-white/[0.07]">
                  <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
                    <i className="ri-image-line text-amber-400 text-xs md:text-sm"></i>
                  </div>
                  <span className="text-amber-400 text-[10px] md:text-xs font-medium">이미지 첨부됨</span>
                </div>
              )}
              <p className="text-gray-200 text-xs md:text-sm leading-relaxed whitespace-pre-wrap">{item.message}</p>
            </div>
          </div>

          {/* Recipients list */}
          <div>
            <div className="flex items-center justify-between mb-2 md:mb-3">
              <p className="text-gray-400 text-[10px] md:text-xs font-medium">수신자 목록</p>
              {item.status !== "scheduled" && recipients.length > 0 && (
                <div className="flex items-center gap-0.5 md:gap-1">
                  {(["all", "success", "fail"] as const).map((f) => (
                    <button
                      key={f}
                      onClick={() => setRecipientFilter(f)}
                      className={`px-2 py-0.5 md:px-2.5 md:py-1 rounded-full text-[10px] md:text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
                        recipientFilter === f
                          ? "bg-gradient-to-r from-violet-600 to-pink-600 text-white"
                          : "bg-white/[0.05] text-gray-500 hover:text-white"
                      }`}
                    >
                      {f === "all" ? "전체" : f === "success" ? "성공" : "실패"}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden max-h-44 md:max-h-52 overflow-y-auto">
              {recipientsLoading ? (
                <div className="flex items-center justify-center gap-2 py-6 md:py-8">
                  <div className="w-4 h-4 border-2 border-violet-500/40 border-t-violet-500 rounded-full animate-spin"></div>
                  <p className="text-gray-500 text-xs">수신자 목록 불러오는 중...</p>
                </div>
              ) : filteredRecipients.length === 0 ? (
                <div className="text-center py-5 md:py-7">
                  {recipients.length === 0 && totalCount > 0 ? (
                    <>
                      <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center mx-auto mb-2 rounded-full bg-amber-500/10">
                        <i className="ri-information-line text-amber-400 text-lg md:text-xl"></i>
                      </div>
                      <p className="text-amber-300 text-xs md:text-sm font-medium mb-1">개별 수신자 데이터 없음</p>
                      <p className="text-gray-500 text-[11px] md:text-xs leading-relaxed max-w-xs mx-auto">
                        {item.status === "success"
                          ? <>발송은 정상 완료되었으나<br/>개별 수신자 상세 결과가 저장되지 않았습니다</>
                          : item.status === "pending"
                          ? <>발송이 아직 처리 중이거나<br/>처리 도중 중단되어 데이터가 생성되지 않았습니다</>
                          : <>발송이 처리되기 전에 중단되어<br/>개별 수신자 결과가 저장되지 않았습니다</>
                        }
                      </p>
                    </>
                  ) : (
                    <p className="text-gray-500 text-xs md:text-sm">해당 수신자가 없습니다</p>
                  )}
                </div>
              ) : (
                filteredRecipients.map((r, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between px-3 md:px-4 py-2 md:py-2.5 border-b border-white/[0.04] last:border-0 hover:bg-white/[0.03] transition-colors"
                  >
                    <div className="flex items-center gap-2 md:gap-2.5">
                      <div className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-full bg-gradient-to-br from-violet-500/30 to-pink-500/30 text-white text-[10px] md:text-xs font-semibold shrink-0">
                        {r.name ? r.name.charAt(0) : r.phone.charAt(0)}
                      </div>
                      <div>
                        {r.name && <p className="text-white text-xs md:text-sm font-medium">{r.name}</p>}
                        <p className="text-gray-500 text-[10px] md:text-xs">{r.phone}</p>
                        {r.status === "fail" && r.failReason && (
                          <p className="text-red-400/80 text-[10px] mt-0.5 flex items-center gap-1">
                            <i className="ri-error-warning-line text-[10px]"></i>
                            {r.failReason}
                          </p>
                        )}
                      </div>
                    </div>
                    {item.status !== "scheduled" && (
                      <span className={`text-[10px] md:text-xs font-medium px-1.5 py-0.5 md:px-2 md:py-0.5 rounded-full whitespace-nowrap ${
                        r.status === "success"
                          ? "bg-emerald-500/15 text-emerald-400"
                          : r.status === "fail"
                          ? "bg-red-500/15 text-red-400"
                          : "bg-amber-500/15 text-amber-400"
                      }`}>
                        {r.status === "success" ? "성공" : r.status === "fail" ? "실패" : "발송 중"}
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="border-t border-white/[0.08] p-3 md:p-4 flex items-center gap-2 md:gap-3 shrink-0">
          {item.status === "scheduled" ? (
            <>
              <button
                onClick={() => setCancelConfirm(true)}
                className="flex-1 py-2 md:py-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 text-xs md:text-sm font-semibold cursor-pointer whitespace-nowrap transition-all flex items-center justify-center gap-1.5 md:gap-2"
              >
                <i className="ri-calendar-close-line text-sm md:text-base"></i>
                예약 취소
              </button>
              <button
                onClick={onClose}
                className="flex-1 py-2 md:py-2.5 rounded-xl bg-white/[0.05] border border-white/[0.08] text-gray-300 hover:text-white text-xs md:text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
              >
                닫기
              </button>
            </>
          ) : (
            <>
              {item.failCount > 0 && (
                <button
                  onClick={handleResendToFailed}
                  className="flex items-center gap-1 px-3 py-2 md:px-4 md:py-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 text-xs md:text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
                >
                  <i className="ri-refresh-line text-sm md:text-base"></i>
                  실패 재발송
                </button>
              )}
              <button
                onClick={handleResend}
                disabled={resending || resent}
                className={`flex-1 py-2 md:py-2.5 rounded-xl text-xs md:text-sm font-bold cursor-pointer whitespace-nowrap transition-all flex items-center justify-center gap-1.5 md:gap-2 ${
                  resent
                    ? "bg-emerald-500/20 border border-emerald-500/30 text-emerald-400"
                    : "bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 disabled:opacity-50 text-white"
                }`}
              >
                {resent ? (
                  <><i className="ri-checkbox-circle-line text-sm md:text-base"></i>재발송 완료!</>
                ) : resending ? (
                  <><i className="ri-loader-4-line animate-spin text-sm md:text-base"></i>발송 중...</>
                ) : (
                  <><i className="ri-send-plane-line text-sm md:text-base"></i>동일 내용 재발송</>
                )}
              </button>
              <button
                onClick={onClose}
                className="px-3 py-2 md:px-4 md:py-2.5 rounded-xl bg-white/[0.05] border border-white/[0.08] text-gray-400 hover:text-white text-xs md:text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
              >
                닫기
              </button>
              <button
                onClick={() => setDeleteConfirm(true)}
                disabled={deleting}
                className="px-3 py-2 md:px-4 md:py-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 text-xs md:text-sm font-semibold cursor-pointer whitespace-nowrap transition-all flex items-center gap-1"
              >
                <i className={`${deleting ? "ri-loader-4-line animate-spin" : "ri-delete-bin-6-line"} text-sm md:text-base`}></i>
                <span className="hidden sm:inline">삭제</span>
              </button>
            </>
          )}
        </div>

        {/* Cancel confirm overlay */}
        {cancelConfirm && (
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6 rounded-2xl">
            <div className="bg-[#0d0d1a] border border-white/[0.12] rounded-2xl p-6 w-full max-w-sm text-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-500/20 mx-auto mb-4">
                <i className="ri-error-warning-line text-red-400 text-2xl"></i>
              </div>
              <h3 className="text-white font-bold text-base mb-2">예약 발송을 취소할까요?</h3>
              <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                취소 후에는 되돌릴 수 없습니다.<br />예약된 발송이 삭제됩니다.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setCancelConfirm(false)}
                  className="flex-1 py-2.5 rounded-xl bg-white/[0.06] text-gray-300 hover:text-white text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
                >
                  돌아가기
                </button>
                <button
                  onClick={handleCancel}
                  className="flex-1 py-2.5 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 text-sm font-bold cursor-pointer whitespace-nowrap transition-all"
                >
                  예약 취소
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete confirm overlay */}
        {deleteConfirm && (
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6 rounded-2xl">
            <div className="bg-[#0d0d1a] border border-white/[0.12] rounded-2xl p-6 w-full max-w-sm text-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-500/20 mx-auto mb-4">
                <i className="ri-delete-bin-6-line text-red-400 text-2xl"></i>
              </div>
              <h3 className="text-white font-bold text-base mb-2">발송 내역을 삭제할까요?</h3>
              <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                삭제 후에는 되돌릴 수 없습니다.<br />이 발송 기록이 영구적으로 삭제됩니다.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(false)}
                  className="flex-1 py-2.5 rounded-xl bg-white/[0.06] text-gray-300 hover:text-white text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
                >
                  취소
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 py-2.5 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 text-sm font-bold cursor-pointer whitespace-nowrap transition-all flex items-center justify-center gap-1.5 disabled:opacity-50"
                >
                  {deleting ? (
                    <><i className="ri-loader-4-line animate-spin"></i>삭제 중...</>
                  ) : (
                    "삭제"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}